import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Routes as SalesRoutes } from "ui/modules/sales/navigation/routes";
import { transformRoute, useTranslate } from "helpers";

import {
  FleetVehicleRow,
  FleetVehicleSearchFormValues,
} from "services/api/fleet/vehicles/types";
import { getListingVehiclesAPI } from "services/api/sales/listings/api";
import { VehicleListingRow } from "services/api/sales/listings/types";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";

import { ListingSearchPageCmp } from "./types";
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { Pagination } from "services/search";
import { NoResults } from "ui/components/NoResults";
import useQueryParam from "services/hooks/useQueryParam";

import ListingSearchFilters from "./components/ListingSearchFilters/ListingSearchFilters";
import ListingSearchResults from "./components/ListingSearchResults/ListingSearchResults";
import ListingEditCard from "ui/modules/sales/pages/listings/components/ListingEditCard/ListingEditCard";
import { useSelectedStringItemsChanges } from "ui/modules/fleet/vehicles/pages/VehicleSearchPage/hooks";

import { SALES_LISTING_SEARCH_PAGE_TITLE } from "ui/components/PageTitle/consts";

const ListingSearchPage: ListingSearchPageCmp = () => {
  const { t } = useTranslate();
  const navigate = useNavigate();

  const [searchResults, setSearchResults] = useState<DataWithPagination<FleetVehicleRow>>(initialDataWithPagination);
  const [listings, setListings] = useState<VehicleListingRow[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // viewType u URL-u kao `?view=table|grid` — preživljava reload i shareable
  // je za bookmark.
  const [viewType, setViewType] = useQueryParam("view", "table", ["table", "grid"] as const);
  const [publishedFilter, setPublishedFilter] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [newListingVehicle, setNewListingVehicle] = useState<FleetVehicleRow | null>(null);

  const handleSelectedChange = useSelectedStringItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handleNewListing = (row: FleetVehicleRow) => {
    setNewListingVehicle(row);
  };

  const handleNewListingSuccess = (listingId: string) => {
    setNewListingVehicle(null);
    navigate(transformRoute(SalesRoutes.ListingDetailPageRouter, { id: listingId }));
  };

  const handleNewListingClose = () => {
    setNewListingVehicle(null);
  };

  useEffect(() => {
    fetchSearchResult(searchResults.pagination);
  }, []);

  const fetchSearchResult = async (
    pagination: Pagination,
    name?: string | null,
    vehicleManufacturerId?: string | null,
    vehicleModelId?: string | null,
    vehicleTypeId?: string | null,
    published?: string | null
  ) => {
    setIsLoading(true);
    try {
      const results = await getListingVehiclesAPI(
        pagination,
        name,
        vehicleManufacturerId,
        vehicleModelId,
        vehicleTypeId,
        published
      );
      setSearchResults(results.vehicles);
      setListings(results.listings);
    } catch {
      setSearchResults(initialDataWithPagination);
      setListings([]);
    }
    setIsLoading(false);
  };

  const handleRowClick = (id: string) => {
    const existingListing = listings.find((l) => l.fleetVehicleId === id);
    if (existingListing) {
      navigate(transformRoute(SalesRoutes.ListingDetailPageRouter, { id: existingListing.id }));
      return;
    }

    // Vozilo još nema listing — otvori wizard za unos novog oglasa (isti tok kao
    // "Unesi novi oglas" u kebab izborniku). Bare create bez base podataka BE
    // odbija, pa korisnik mora proći kroz wizard.
    const vehicle = searchResults.data.find((v) => v.id === id);
    if (vehicle) handleNewListing(vehicle);
  };

  useEffect(() => {
    fetchSearchResult(searchResults.pagination, null, null, null, null, publishedFilter);
  }, [publishedFilter]);

  const handleSearchChange = (formValues: FleetVehicleSearchFormValues) => {
    fetchSearchResult(
      searchResults.pagination,
      formValues.search,
      formValues.vehicleManufacturerId,
      formValues.vehicleModelId,
      formValues.vehicleTypeId,
      publishedFilter
    );
  };

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination);
  };

  return (
    <>
      <div>
        <div className="bg-white rounded-t-md border border-b mb-2">
          <PageHeader>
            <div className="flex items-center justify-between">
              <div>
                <PageTitle
                  title={SALES_LISTING_SEARCH_PAGE_TITLE.text}
                  icon={SALES_LISTING_SEARCH_PAGE_TITLE.icon}
                  iconColor={SALES_LISTING_SEARCH_PAGE_TITLE.iconColor}
                />
                <Breadcrumb />
              </div>
            </div>
          </PageHeader>
        </div>
        <div className="px-0 pt-2 mx-[-1px] xl:px-6">
          <ListingSearchFilters
            onSearchChange={handleSearchChange}
            viewType={viewType}
            onViewTypeChange={(v) => setViewType(v as "table" | "grid")}
            publishedFilter={publishedFilter}
            onPublishedFilterChange={setPublishedFilter}
          />

          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message={t("admin.sales.listing.search.noresults")} />
          ) : (
            <ListingSearchResults
              searchResults={searchResults}
              listings={listings}
              isLoading={isLoading}
              viewType={viewType}
              selectableRows={true}
              selectedRows={selectedRows}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
              onNewListing={handleNewListing}
            />
          )}
        </div>
      </div>
      <ListingEditCard
        show={!!newListingVehicle}
        fleetVehicleId={newListingVehicle?.id ?? null}
        fleetVehicleName={newListingVehicle?.name ?? null}
        onSuccess={handleNewListingSuccess}
        onClose={handleNewListingClose}
      />
    </>
  );
};

ListingSearchPage.route = SalesRoutes.ListingSearchPageRouter;

export default ListingSearchPage;
